import * as React from "react";
import { useEffect } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerClose } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";

/**
 * ResponsiveModal - Mobile-optimized modal component
 * 
 * Features:
 * - Uses Drawer (bottom sheet) on mobile (< 768px)
 * - Uses Dialog (centered) on desktop
 * - Prevents accidental outside dismissal on mobile
 * - Intercepts back button to close modal instead of navigating
 * - 85vh height on mobile with safe zone padding
 * - Large touch targets (48px minimum)
 * - No swipe-to-dismiss (explicit close button only)
 */

const useMediaQuery = (query) => {
  const [matches, setMatches] = React.useState(false);

  React.useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    const listener = () => setMatches(media.matches);
    media.addEventListener("change", listener);
    return () => media.removeEventListener("change", listener);
  }, [matches, query]);

  return matches;
};

export function ResponsiveModal({
  open,
  onOpenChange,
  title,
  description,
  children,
  className,
  preventBackNavigation = true,
  showCloseButton = true,
  maxWidth = "sm:max-w-lg",
  mobileHeight = "85vh", // 85% for peek effect
  ...props
}) {
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Prevent back navigation when modal is open on mobile
  useEffect(() => {
    if (!open || !preventBackNavigation || !isMobile) return;

    const handlePopState = (e) => {
      e.preventDefault();
      onOpenChange(false);
      // Push state back so we don't actually navigate
      window.history.pushState(null, "", window.location.href);
    };

    // Push a state when modal opens
    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [open, preventBackNavigation, onOpenChange, isMobile]);

  if (isMobile) {
    return (
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
        dismissible={false} // No swipe to dismiss
        {...props}
      >
        <DrawerContent
          className={cn(
            // Override the base h-auto with explicit height
            "!h-[90vh]", // Important to override h-auto from drawer.jsx
            "flex flex-col",
            "pb-safe", // Safe zone padding for iPhone home indicator
            className
          )}
          onInteractOutside={(e) => {
            // Prevent closing when tapping outside on mobile
            e.preventDefault();
          }}
        >
          {/* Note: Base DrawerContent already includes a drag handle, so we don't add another */}
          
          <DrawerHeader className="text-left relative pb-2 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex-1 pr-10">
                {title && <DrawerTitle className="text-xl">{title}</DrawerTitle>}
                {description && <DrawerDescription className="mt-2">{description}</DrawerDescription>}
              </div>
              {showCloseButton && (
                <DrawerClose asChild>
                  <Button
                    variant="ghost"
                    className="absolute right-4 top-4 h-8 w-8 p-0 rounded-full"
                    onClick={() => onOpenChange(false)}
                  >
                    <X className="h-5 w-5" />
                    <span className="sr-only">Close</span>
                  </Button>
                </DrawerClose>
              )}
            </div>
          </DrawerHeader>

          {/* Content with padding but NO wrapper div - children scroll directly */}
          <div className="flex-1 min-h-0 px-4 pb-4" style={{ overflowY: 'auto', overflowX: 'hidden' }}>
            {children}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  // Desktop: Standard Dialog
  return (
    <Dialog open={open} onOpenChange={onOpenChange} {...props}>
      <DialogContent
        className={cn(
          "max-w-[calc(100%-2rem)]",
          maxWidth,
          "max-h-[90vh]",
          "!overflow-hidden", // Override base DialogContent overflow
          "flex flex-col",
          "p-0", // Remove default padding, we'll add it to sections
          className
        )}
      >
        {(title || description) && (
          <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
            {title && <DialogTitle>{title}</DialogTitle>}
            {description && <DialogDescription>{description}</DialogDescription>}
          </DialogHeader>
        )}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6">
          {children}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Touch-friendly button wrapper for mobile
export function ResponsiveButton({ children, className, ...props }) {
  return (
    <Button
      className={cn(
        "min-h-[48px] md:min-h-[44px]", // 48px on mobile, 44px on desktop
        "text-base", // 16px minimum for mobile
        className
      )}
      {...props}
    >
      {children}
    </Button>
  );
}

export default ResponsiveModal;
